import { useState } from 'react';
import { Dialog, DialogContent, DialogDescription, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { BookCheck, ChevronLeft, Coins, HelpCircle, MessageSquareWarning } from 'lucide-react';
import { SetupStepper } from '@/components/study/SetupStepper';
import { HowToEarnDialog } from '@/components/study/HowToEarnDialog';

/** Final setup step. Study Buddy has mapped the syllabus it believes is theirs —
 *  this is where the student either confirms it or tells us it's wrong. */
export function SyllabusMappingStep({ open, step = 5, totalSteps = 5, board = '', classLevel = '', school = '', onConfirm, onMismatch, onBack }) {
  const [howOpen, setHowOpen] = useState(false);
  return (
    <>
      <Dialog open={open} onOpenChange={o => { if (!o) onConfirm?.(); }}>
        <DialogContent className="max-w-xl rounded-3xl p-0 overflow-hidden"
          data-testid="syllabus-mapping-step">
          <div className="px-6 pt-6 pb-3 text-center">
            <SetupStepper step={step} total={totalSteps}
              labels={['Mode Of Study', 'School & Class', 'Study theme', 'Choose your plan', 'Your syllabus']} />
            <DialogTitle className="font-heading text-2xl font-bold text-slate-900 mt-2">
              One last thing — how to tell us if something looks wrong
            </DialogTitle>
            <DialogDescription className="text-sm text-slate-500 font-body mt-1.5">
              Your Study Buddy has lined up every chapter against {[board, classLevel && `Class ${classLevel}`, school].filter(Boolean).join(' · ') || 'your school'}.
              You don’t have to check anything now — just remember this for the day something doesn’t match your book.
            </DialogDescription>
          </div>

          <div className="px-6 space-y-3">
            <div className="rounded-2xl border border-sky-200 bg-sky-50/60 p-3.5">
              <p className="font-heading text-[13px] text-sky-900 flex items-center gap-2">
                <MessageSquareWarning className="w-4 h-4" /> Just tell your Study Buddy
              </p>
              <p className="text-[12px] font-body text-sky-900/80 mt-0.5">
                Type it in the chat the way you’d say it out loud — “Chapter 4 isn’t in my book”,
                “where do I upload my syllabus?”, “show me my Veda Coins”. Your Study Buddy takes you
                straight to the right place; you never have to hunt for a screen.
              </p>
            </div>
            <div className="rounded-2xl border border-amber-200 bg-amber-50/60 p-3.5">
              <p className="font-heading text-[13px] text-amber-900 flex items-center gap-2">
                <BookCheck className="w-4 h-4" /> And you get paid for helping
              </p>
              <p className="text-[12px] font-body text-amber-900/80 mt-0.5">
                Upload the chapter your book actually has and
                <span className="font-heading"> earn 100 Veda Coins</span> once we approve it.
              </p>
              <button type="button" data-testid="mapping-how-link" onClick={() => setHowOpen(true)}
                className="mt-1.5 inline-flex items-center gap-1 text-[11px] font-heading text-amber-800 hover:underline">
                <HelpCircle className="w-3 h-3" /> Click here to know how
                <Coins className="w-3 h-3" />
              </button>
            </div>
          </div>

          <div className="flex items-center gap-2 px-6 py-5">
            {onBack && (
              <Button variant="ghost" size="sm" className="rounded-full font-heading text-xs"
                data-testid="mapping-back-btn" onClick={onBack}>
                <ChevronLeft className="w-3.5 h-3.5 mr-1" /> Back
              </Button>
            )}
            <Button variant="outline" size="sm" className="rounded-full font-heading text-xs ml-auto"
              data-testid="mapping-mismatch-btn" onClick={onMismatch}>
              Upload a chapter now
            </Button>
            <Button size="sm" className="rounded-full font-heading text-xs"
              data-testid="mapping-confirm-btn" onClick={onConfirm}>
              Got it — start learning
            </Button>
          </div>
        </DialogContent>
      </Dialog>
      <HowToEarnDialog open={howOpen} onOpenChange={setHowOpen} />
    </>
  );
}

export default SyllabusMappingStep;
